import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/profase-periodo-actividad.reducer';
import {
  buildPeriodoActividadPayload,
  defaultPeriodoActividadFormValues,
  periodoActividadFormFromEntity,
  type PeriodoActividadFormValues,
  type ProfasePeriodoActividadEntity,
  validatePeriodoActividadForm,
} from '../../../../shared/profase-periodo-actividad';
import PeriodoActividadFormFields from '../../../workflow/periodo-actividad-form-fields';

const BACK = '/configuracion/calendario/periodos/definicion-periodo';
const PERIODOS = '/configuracion/calendario/periodos';

export default function DefinicionPeriodoFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.profasePeriodoActividad.entity) as ProfasePeriodoActividadEntity;
  const updating = useAppSelector(s => s.profasePeriodoActividad.updating);
  const updateSuccess = useAppSelector(s => s.profasePeriodoActividad.updateSuccess);
  const errorMessage = useAppSelector(s => s.profasePeriodoActividad.errorMessage);

  const [form, setForm] = useState<PeriodoActividadFormValues>(() => defaultPeriodoActividadFormValues());
  const [errors, setErrors] = useState<Partial<Record<keyof PeriodoActividadFormValues, string>>>({});

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(defaultPeriodoActividadFormValues());
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm(periodoActividadFormFromEntity(entity));
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Definición de periodo creada correctamente.'
            : 'Definición de periodo actualizada correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage, showToast]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validatePeriodoActividadForm(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = buildPeriodoActividadPayload(form, isNew ? undefined : Number(id));

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Periodos', to: PERIODOS, icon: <FaRegCalendarCheck /> },
        { label: 'Definición de Periodo', to: BACK, icon: <FaEdit /> },
        { label: isNew ? 'Nueva definición' : `Editar definición #${id}` },
      ]} />

      <PageHeader
        icon={<FaEdit />}
        iconColor="#10b981"
        title={isNew ? 'Nueva Definición de Periodo' : 'Editar Definición de Periodo'}
        subtitle={isNew
          ? 'Define qué tipo y número de periodo activan instancias de actividad'
          : `Modificando definición #${id}`}
      />

      {alert && <PageAlert alert={alert} />}

      <form className="profase-form-card" onSubmit={handleSubmit}>
        {!isNew ? (
          <div style={s.idRow}>
            <span className="profase-form-label">ID</span>
            <span>{id}</span>
          </div>
        ) : null}

        <PeriodoActividadFormFields
          form={form}
          errors={errors}
          disabled={updating}
          onChange={setForm}
          onClearError={key => setErrors(prev => ({ ...prev, [key]: undefined }))}
        />

        <div className="profase-form-actions">
          <button type="button" className="profase-form-btn-secondary" onClick={() => navigate(BACK)} disabled={updating}>
            <FaArrowLeft style={{ marginRight: 6 }} />
            Volver
          </button>
          <button type="submit" className="profase-form-btn-primary" style={{ background: '#10b981' }} disabled={updating}>
            <FaSave style={{ marginRight: 6 }} />
            {updating ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  idRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 12,
    fontSize: 13,
    color: 'var(--crm-body-text)',
  },
};
